<template>
  <div class="fox-invoice-create__wrapper">
    <div class="fox-invoice-create__container">
      <div class="tw-mb-10 tw-flex tw-items-start tw-flex-col md:tw-flex-row tw-gap-4 md:tw-gap-6">
        <div class="tw-flex tw-flex-col tw-gap-2">
          <a href="#" @click.prevent="invoiceExample" class="link-button">
            Beispiel-Rechnung
          </a>

          <div class="tw-flex tw-flex-col tw-gap-1">
            <span class="tw-font-bold">Abonnement</span>

            <div class="tw-relative">
              <select v-model="invoice.subscription" class="fox-input--simple fox-input--simple-select tw-w-full" style="min-width: 170px;">
                <option selected value="">keins</option>
                <option value="MONTHLY">Monatlich</option>
                <option value="YEARLY">Jährlich</option>
              </select>

              <div class="tw-absolute tw-inset-y-0 tw-right-1 tw-flex tw-items-center tw-pointer-events-none">
                <v-icon>mdi-menu-down</v-icon>
              </div>
            </div>
          </div>
        </div>

        <div class="fox-invoice-action__container">
          <button @click="preview" class="fox-invoice-action__create-btn fox-invoice-action__create-btn--sky-blue tw-flex tw-items-center tw-shadow-lg">
            <FocusIcon />
            <span class="tw-text-black tw-font-bold">Vorschau</span>
          </button>
          <button @click="sendAsEnvelop" class="fox-invoice-action__create-btn fox-invoice-action__create-btn--green tw-flex tw-items-center">
            <LetterIcon />
            <span class="tw-text-white">als Brief Versenden</span>
          </button>
          <button @click="downloadPdf" class="fox-invoice-action__create-btn fox-invoice-action__create-btn--default">
            <PdfIcon />
            <span class="tw-text-white">Download - PDF</span>
          </button>
          <button @click="downloadCombined" class="fox-invoice-action__create-btn fox-invoice-action__create-btn--purple">
            <DownloadIcon />
            <span class="tw-text-black">Download - ZUGFeRD</span>
          </button>
          <button @click="downloadXml" class="fox-invoice-action__create-btn fox-invoice-action__create-btn--purple">
            <DownloadIcon />
            <span class="tw-text-black">Download - X-Rechnung</span>
          </button>
        </div>
      </div>

      <!-- Main layout grid -->
      <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-4 lg:tw-gap-8">
        <!-- Left Column (Biller Form + Invoice Recipient) -->
        <div class="tw-flex tw-flex-col tw-gap-6">
          <InvoiceBillerForm v-model:biller="invoice.biller" @update="updateBiller" :companyInfo="companyInfo" />
          <InvoiceReceiverForm v-model:receiver="invoice.receiver" @update="updateReceiver" :clients="clients" />
        </div>

        <div>
          <div class="tw-mb-8 tw-flex tw-items-center">
            <div class="tw-w-full tw-h-56 tw-rounded-lg tw-shadow-xl tw-bg-white tw-flex tw-items-center tw-justify-center"
              @click="triggerLogoFileInput"
            >
              <div class="tw-flex tw-items-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="tw-h-10 tw-w-10 tw-mr-3 tw-text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v6h16v-6M12 3v13m0-13L5 10m7-7l7 7" />
                </svg>
                <p class="tw-text-gray-400 tw-font-bold tw-text-xl sm:tw-text-2xl tw-w-full tw-text-center" style="margin-bottom: 0;">Logo hochladen</p>
              </div>
              <input
                type="file"
                ref="fileInput"
                @change="previewImage"
                style="display: none;"
                accept="image/*"
              />
              <div class="upload-content">
                <img
                  v-if="imagePreview"
                  :src="imagePreview"
                  alt="Uploaded Image"
                  class="uploaded-image"
                  style="max-width: 60%"
                />
              </div>
            </div>
          </div>

          <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4">
            <div>
              <label class="fox-label--simple">
                Betreff<span class="fox-input__require-star">*</span>
              </label>
              <input type="text"
                class="fox-input--simple tw-w-full"
                v-model="invoice.subject"
                :placeholder="placeholder.subject"
                v-bind:class="classes.subject"
              >
            </div>
            <!-- Rechnungsnummer -->
            <div>
              <label class="fox-label--simple">
                Rechnungsnummer<span class="fox-input__require-star">*</span>
              </label>
              <input type="text"
                v-model="invoice.invoiceNo"
                class="fox-input--simple tw-w-full"
                :placeholder="placeholder.invoiceNo"
                v-bind:class="classes.invoiceNo"
              >
            </div>
            <!-- Rechnungsdatum -->
            <div>
              <label class="fox-label--simple">
                Rechnungsdatum<span class="fox-input__require-star">*</span>
              </label>
              <date-picker
                v-model:value="invoice.invoiceDate"
                v-bind:class="classes.invoiceDate"
                type="date"
                format="DD.MM.YYYY"
              />
            </div>
            <!-- Leistungszeitraum -->
            <div>
              <label class="fox-label--simple">
                <span v-show="!isDeliveryDatepickerRange">
                  Leistungszeitraum<span class="fox-input__require-star">*</span>
                </span>
                <span v-show="isDeliveryDatepickerRange">
                  Leistungszeitraum<span class="fox-input__require-star">*</span>
                </span>
              </label>
              <date-picker
                v-model:value="invoice.invoiceDeliveryDate"
                v-bind:class="classes.invoiceDeliveryDate"
                @clear="clearDeliveryDate"
                @calendar-change="clearDeliveryDate"
                type="date"
                :range="isDeliveryDatepickerRange"
                format="DD.MM.YYYY"
              />
              <button
                class="fox-invoice-button__blue-text"
                v-show="isDeliveryDatepickerRange"
                @click="toggleInvoiceDeliveryCalendar"
              >
                + Leistungszeitraum
              </button>
              <button
                class="fox-invoice-button__blue-text"
                v-show="!isDeliveryDatepickerRange"
                @click="toggleInvoiceDeliveryCalendar"
              >
                + Leistungszeitraum
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="fox-invoice-create__divider"></div>

      <div>
        <InvoiceRichTextInput @change="updateHeadText" :value="invoice.headText"/>
      </div>

      <div class="fox-invoice-create__divider"></div>

      <div class="tw-flex tw-flex-col tw-gap-4">
        <InvoiceItemContainer @updateInvoiceItems="updateInvoiceItems" :initial-items="invoice.invoiceItems" />

        <div class="tw-flex">
          <div class="tw-flex tw-flex-col fox-width-20"></div>

          <div class="tw-flex tw-flex-col">
            <span class="tw-font-bold">Zahlungsfrist</span>

            <div class="tw-flex tw-gap-2">
              <AutoNumericInput
                class="fox-input--simple fox-width-50"
                v-model="invoice.deadline.number"
                :options="autonumericOptions.deadline"
              />

              <div class="tw-relative fox-width-200">
                <select v-model="invoice.deadline.unit" class="fox-input--simple fox-input--simple-select tw-w-full">
                  <option value="" selected></option>
                  <option value="DAYS">Tage</option>
                  <option value="MONTHS">Monate</option>
                </select>

                <div class="tw-absolute tw-inset-y-0 tw-right-1 tw-flex tw-items-center tw-pointer-events-none">
                  <v-icon>mdi-menu-down</v-icon>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="fox-invoice-create__divider"></div>

      <div>
        <InvoiceRichTextInput @change="updateBottomText" :value="computedBottomText"  />
      </div>

      <div class="fox-invoice-create__divider"></div>

      <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4 lg:tw-gap-6">
        <div class="tw-w-full">
          <label class="fox-label--simple">
            Kontakt
          </label>
          <textarea name="customer_address_plain"
                    rows="5"
                    :placeholder="placeholder.contactDataPlain"
                    class="fox-input--simple tw-w-full"
                    v-model="invoice.contactDataPlain"></textarea>
        </div>

        <div class="tw-w-full">
          <label class="fox-label--simple">
            Rechtliches
          </label>
          <textarea name="customer_address_plain"
                    rows="5"
                    :placeholder="placeholder.legalDetailsPlain"
                    class="fox-input--simple tw-w-full"
                    v-model="invoice.legalDetailsPlain"></textarea>
        </div>

        <div class="tw-w-full">
          <label class="fox-label--simple">
            Bankverbindung
          </label>
          <textarea name="customer_address_plain"
                    rows="5"
                    :placeholder="placeholder.bankDetailsPlain"
                    class="fox-input--simple tw-w-full"
                    v-model="invoice.bankDetailsPlain"></textarea>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import InvoiceItemContainer from '@/components/Invoice/Create/InvoiceItemContainer'
import DatePicker from 'vue-datepicker-next'
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import InvoiceRichTextInput from '@/components/Invoice/Create/InvoiceRichTextInput'
import $ from 'jquery'
import InvoiceBillerForm from '@/components/Invoice/Create/InvoiceBillerForm'
import InvoiceReceiverForm from '@/components/Invoice/Create/InvoiceReceiverForm'
import FocusIcon from '@/components/Icon/FocusIcon.vue'
import LetterIcon from '@/components/Icon/LetterIcon.vue'
import PdfIcon from '@/components/Icon/PdfIcon.vue'
import DownloadIcon from '@/components/Icon/DownloadIcon.vue'
import ApiService from '@/services/api.service'
import AutoNumericInput from '@/components/Util/AutoNumericInput.vue'

export default {
  name: 'InvoiceForm',
  components: { InvoiceBillerForm, InvoiceReceiverForm, InvoiceRichTextInput, InvoiceItemContainer, DatePicker, FocusIcon, LetterIcon, PdfIcon, DownloadIcon, AutoNumericInput },
  setup: () => ({ v$: useVuelidate() }),
  validations () {
    return {
      invoice: {
        subject: { required },
        invoiceNo: { required },
        invoiceDate: { required },
        invoiceDeliveryDate: { required }
      }
    }
  },
  mounted () {
    this.fetchUserCompany()
    this.fetchClients()

    const saved = localStorage.getItem('invoiceDraft')
    if (saved) {
      Object.assign(this.invoice, JSON.parse(saved))
    }
  },
  data: function () {
    return {
      invoice: {
        biller: {
          company: null,
          name: null,
          street: null,
          postalCode: null,
          city: null,
          country: null,
          uuid: null
        },
        receiver: {
          company: null,
          name: null,
          street: null,
          postalCode: null,
          city: null,
          country: null,
          uuid: null
        },
        subject: null,
        invoiceNo: null,
        invoiceDate: null,
        invoiceDeliveryDate: null,
        headText: '<div class="page" title="Page 1">\n<div class="layoutArea">\n<div class="column">\n<p>Sehr geehrte Damen und Herren,</p>\n<p>vielen Dank für Ihren Auftrag und das damit verbundene Vertrauen! Hiermit stelle ich Ihnen die folgenden Leistungen in Rechnung:</p>\n</div>\n</div>\n</div>',
        bottomText: '<div class="page" title="Page 1">\n<div class="layoutArea">\n<div class="column">\n<p>Bitte &uuml;berweisen Sie den Rechnungsbetrag unter Angabe der Rechnungsnummer auf das unten angegebene Konto.</p>\n<p class="replace-deadline">Der Rechnungsbetrag ist sofort f&auml;llig.</p>\n<p>&nbsp;</p>\n<p>Mit freundlichen Gr&uuml;&szlig;en</p>\n</div>\n</div>\n</div>',
        currency: 'EUR',
        contactDataPlain: null,
        legalDetailsPlain: null,
        bankDetailsPlain: null,
        logoFile: null,
        invoiceItems: [
          {
            position: 1,
            description: null,
            quantity: null,
            unit: null,
            unitPrice: null,
            vatPercentage: null
          }
        ],
        subscription: '',
        deadline: {
          number: null,
          unit: null
        }
      },
      isDeliveryDatepickerRange: false,
      placeholder: {
        subject: 'Rechnung RE-2022-08-01',
        invoiceNo: 'RE-2022-08-01',
        IBAN: 'DE17100500001258767',
        accountHolder: 'Max Mustermann',
        contactDataPlain: 'Tel.: 154 1232 4923\nE-Mail: contact@max-mustermann.com\nWeb: www.max-mustermann.de',
        legalDetailsPlain: 'USt.-ID: DE12345893\nInhaber/-in: Max Musterman',
        bankDetailsPlain: 'Berliner Volksbank\nIBAN: DE91100000000123456789\nBIC: BEVODEBB'
      },
      imagePreview: null,
      companyInfo: null,
      clients: null,
      autonumericOptions: {
        deadline: {
          minimumValue: '0',
          maximumValue: '100',
          decimalPlaces: 0
        }
      }
    }
  },
  computed: {
    classes: function () {
      return {
        subject: {
          'fox-input--error': this.v$.invoice.subject.$error
        },
        invoiceNo: {
          'fox-input--error': this.v$.invoice.invoiceNo.$error
        },
        invoiceDate: {
          'fox-input--error': this.v$.invoice.invoiceDate.$error
        },
        invoiceDeliveryDate: {
          'fox-input--error': this.v$.invoice.invoiceDeliveryDate.$error,
          'mx-calendar-small-text': this.isDeliveryDatepickerRange
        }
      }
    },
    computedBottomText () {
      const { bottomText, deadline } = this.invoice

      const hasDeadline = deadline?.number && deadline?.unit
      if (!hasDeadline) return bottomText

      const unitMap = {
        DAYS: 'tagen',
        MONTHS: 'monaten'
      }
      const unitText = unitMap[deadline.unit] || deadline.unit.toLowerCase()

      const replacement = `<p class="replace-deadline">Die Rechnung ist binnen ${deadline.number} ${unitText} zahlbar.</p>`

      return bottomText.replace(
        /<p class="replace-deadline">.*?<\/p>/s,
        replacement
      )
    }
  },
  methods: {
    async fetchUserCompany () {
      try {
        const response = await ApiService.get('/company/get')
        this.companyInfo = response.data
      } catch (error) {
        console.error('Error fetching company:', error)
      }
    },
    async fetchClients () {
      try {
        const response = await ApiService.get('/client/search', {
          params: {
            limit: -1
          }
        })
        this.clients = response.data.data
      } catch (error) {
        console.error('Error fetching clients:', error)
      }
    },
    updateInvoiceItems (items) {
      this.invoice.invoiceItems = items
    },
    updateBottomText (text) {
      this.invoice.bottomText = text
    },
    updateHeadText (text) {
      this.invoice.headText = text
    },
    updateBiller (biller) {
      const { logoUrl, ...billerWithoutLogoUrl } = biller
      this.invoice.biller = billerWithoutLogoUrl

      if (!this.invoice.logoFile) {
        this.invoice.logoFile = logoUrl || null
      }
    },
    updateReceiver (receiver) {
      this.invoice.receiver = receiver
    },
    clearDeliveryDate () {
      this.invoice.invoiceDeliveryDate = null
    },
    toggleInvoiceDeliveryCalendar () {
      this.isDeliveryDatepickerRange = !this.isDeliveryDatepickerRange
      this.invoice.invoiceDeliveryDate = null
    },
    triggerLogoFileInput () {
      this.$refs.fileInput.click()
    },
    previewImage (event) {
      const file = event.target.files[0]
      console.log(file)
      if (file) {
        const reader = new FileReader()
        reader.onload = (e) => {
          this.imagePreview = e.target.result
        }
        reader.readAsDataURL(file)
        this.invoice.logoFile = file
      }
    },
    async invoiceExample () {
      this.$emit('invoiceExample')
    },
    async preview () {
      const isFormCorrect = await this.v$.$validate()

      if (isFormCorrect) {
        this.$emit('preview', this.invoice)
      } else {
        this.scrollToFirstError()
      }
    },
    async downloadPdf () {
      const isFormCorrect = await this.v$.$validate()

      if (isFormCorrect) {
        this.$emit('downloadPdf', this.invoice)
      } else {
        this.scrollToFirstError()
      }
    },
    async downloadXml () {
      const isFormCorrect = await this.v$.$validate()

      if (isFormCorrect) {
        this.$emit('downloadXml', this.invoice)
      } else {
        this.scrollToFirstError()
      }
    },
    async downloadCombined () {
      const isFormCorrect = await this.v$.$validate()

      if (isFormCorrect) {
        this.$emit('downloadCombined', this.invoice)
      } else {
        this.scrollToFirstError()
      }
    },
    async sendAsEnvelop () {
      const isFormCorrect = await this.v$.$validate()

      if (isFormCorrect) {
        this.$emit('sendAsEnvelope', this.invoice)
      } else {
        this.scrollToFirstError()
      }
    },
    scrollToFirstError () {
      $('html, body').animate({
        scrollTop: $('.fox-input--error:visible:first').offset().top - 100
      }, 800)
    }
  }
}
</script>
